import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function ProfileEnseignant() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({ nom: '', prenom: '', telephone: '', photo_profil_base64: '', photo_preview: null });
  const [passwords, setPasswords] = useState({ old_password: '', new_password: '', confirm_password: '' });
  const fileInputRef = useRef(null);

  const API_BASE = config.apiBase;

  // Normalisation des URLs des photos de profil
  const normalizeProfilePhoto = (url) => config.normalizeProfilePhoto(url);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => { fetchProfile(); }, []);

  const fetchProfile = async () => {
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/auth/get_profile.php`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await response.json();
      if (data.success) {
        const normalizedPhoto = normalizeProfilePhoto(data.user.photo_profil);
        const userData = { ...data.user, photo_profil: normalizedPhoto };
        setUser(userData);
        setFormData({ 
          nom: data.user.nom, 
          prenom: data.user.prenom, 
          telephone: data.user.telephone || '', 
          photo_profil_base64: '', 
          photo_preview: normalizedPhoto 
        });
      }
    } catch (error) { toast.error('Erreur'); } finally { setLoading(false); }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) { 
      const reader = new FileReader(); 
      reader.onloadend = () => setFormData({ ...formData, photo_profil_base64: reader.result, photo_preview: reader.result }); 
      reader.readAsDataURL(file); 
    }
  };

  const updateProfile = async (e) => {
    e.preventDefault();
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/auth/update_profile.php`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
        body: JSON.stringify({ 
          nom: formData.nom, 
          prenom: formData.prenom, 
          telephone: formData.telephone, 
          photo_profil_base64: formData.photo_profil_base64 
        }) 
      });
      const data = await response.json();
      if (data.success) { 
        toast.success('Profil mis à jour'); 
        const normalizedPhoto = normalizeProfilePhoto(data.user.photo_profil);
        const updatedUser = { ...data.user, photo_profil: normalizedPhoto };
        setUser(updatedUser);
        setFormData(prev => ({ ...prev, photo_preview: normalizedPhoto, photo_profil_base64: '' }));
        setEditing(false); 
      } else toast.error(data.message);
    } catch (error) { toast.error('Erreur'); }
  };

  const updatePassword = async (e) => {
    e.preventDefault();
    if (passwords.new_password !== passwords.confirm_password) { toast.error('Les mots de passe ne correspondent pas'); return; }
    if (passwords.new_password.length < 6) { toast.error('Mot de passe trop court (min 6 caractères)'); return; }
    try {
      const token = getToken();
      const response = await fetch(`${API_BASE}/auth/change_password.php`, { 
        method: 'POST', 
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }, 
        body: JSON.stringify({ old_password: passwords.old_password, new_password: passwords.new_password }) 
      });
      const data = await response.json();
      if (data.success) { toast.success('Mot de passe modifié'); setPasswords({ old_password: '', new_password: '', confirm_password: '' }); } 
      else toast.error(data.message);
    } catch (error) { toast.error('Erreur'); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div><p className="mt-4 text-gray-600">Chargement...</p></div></div>;
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">👨‍🏫 Mon profil</h1>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex justify-between items-center mb-4"><h2 className="text-lg font-bold text-gray-800">Informations personnelles</h2>{!editing && <button onClick={() => setEditing(true)} className="text-blue-600 hover:text-blue-700">✏️ Modifier</button>}</div>
          {editing ? (
            <form onSubmit={updateProfile} className="space-y-4">
              <div className="flex justify-center mb-4"><div className="relative"><div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">{formData.photo_preview ? <img src={formData.photo_preview} alt="profil" className="w-full h-full object-cover" /> : <span className="text-3xl">👨‍🏫</span>}</div><button type="button" onClick={() => fileInputRef.current?.click()} className="absolute bottom-0 right-0 bg-blue-600 text-white p-1 rounded-full text-xs">📷</button><input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileChange} /></div></div>
              <div><label className="block text-sm font-medium mb-1">Nom</label><input type="text" value={formData.nom} onChange={(e) => setFormData({...formData, nom: e.target.value})} className="w-full border rounded-lg px-4 py-2" required /></div>
              <div><label className="block text-sm font-medium mb-1">Prénom</label><input type="text" value={formData.prenom} onChange={(e) => setFormData({...formData, prenom: e.target.value})} className="w-full border rounded-lg px-4 py-2" required /></div>
              <div><label className="block text-sm font-medium mb-1">Téléphone</label><input type="tel" value={formData.telephone} onChange={(e) => setFormData({...formData, telephone: e.target.value})} className="w-full border rounded-lg px-4 py-2" /></div>
              <div className="flex gap-3"><button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Enregistrer</button><button type="button" onClick={() => setEditing(false)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300">Annuler</button></div>
            </form>
          ) : (
            <div className="space-y-3"><div className="flex justify-center mb-4"><div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden">{user?.photo_profil ? <img src={user.photo_profil} alt="profil" className="w-full h-full object-cover" /> : <span className="text-3xl">👨‍🏫</span>}</div></div><div><span className="font-semibold">Nom :</span> {user?.nom}</div><div><span className="font-semibold">Prénom :</span> {user?.prenom}</div><div><span className="font-semibold">Email :</span> {user?.email}</div><div><span className="font-semibold">Téléphone :</span> {user?.telephone || '-'}</div><div><span className="font-semibold">Rôle :</span> Enseignant</div></div>
          )}
        </div>
        <div className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">🔒 Changer le mot de passe</h2>
          <form onSubmit={updatePassword} className="space-y-4">
            <div><label className="block text-sm font-medium mb-1">Ancien mot de passe</label><input type="password" value={passwords.old_password} onChange={(e) => setPasswords({...passwords, old_password: e.target.value})} className="w-full border rounded-lg px-4 py-2" required /></div>
            <div><label className="block text-sm font-medium mb-1">Nouveau mot de passe</label><input type="password" value={passwords.new_password} onChange={(e) => setPasswords({...passwords, new_password: e.target.value})} className="w-full border rounded-lg px-4 py-2" required /></div>
            <div><label className="block text-sm font-medium mb-1">Confirmer le mot de passe</label><input type="password" value={passwords.confirm_password} onChange={(e) => setPasswords({...passwords, confirm_password: e.target.value})} className="w-full border rounded-lg px-4 py-2" required /></div>
            <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700">Changer le mot de passe</button>
          </form>
        </div>
      </div>
    </div>
  );
}